// ===============================================================
// 🏠 HomePage.js — Main page (i18n + RTL/LTR compliant + VPN PLAN B)
// - LIVORA PLAN: i18n only (t + defaultValue) + NO dir logic here
// - VPN PLAN (B): show Privacy/Display country (displayCountry / premium.country) instead of real countryKey
// - Filter uses displayCountry (lowercase ISO2 keys)
// - ✅ SILENT BLOCK: hide users I blocked OR who blocked me
// - ✅ FIX: use unified BottomNav so notifications button appears everywhere
// path: src/components/HomePage.js
// ===============================================================

import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import "./HomePage.css";
import { CALL_DEBUG } from "../calls/callDebug";
import {
  canCallUser as canCallUserPolicy,
  isUserBusy,
  resolveCallTapAction,
  resolveDisplayName,
} from "../utils/callAvailability";
import {
  startCallStartupTraceAt,
  logCallStartupEvent,
} from "../calls/callStartupTrace";

import { db, auth } from "../firebase";
import { AuthContext } from "../context/AuthContext";
import { resolveProfilePhotoUrl } from "../utils/profilePhotoResolver";
import { getFunctions, httpsCallable } from "firebase/functions";

import {
  collection,
  query,
  where,
  onSnapshot,
  doc,
  getDocs,
} from "firebase/firestore";

import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

// UI icons
import {
  FaVideo as FaVideoCall,
  FaGlobe,
  FaChevronDown,
  FaSearch,
} from "react-icons/fa";

// ✅ unified bottom nav
import BottomNav from "./BottomNav";
import { useCallStartOverlay } from "../calls/useCallStartOverlay";
import { getStartCallErrorToast } from "../calls/startCallErrors";
import {
  buildReturnContextFromLocation,
  saveReturnContext,
} from "../calls/nativeCallReturnContext";
import useLivoraToast from "../hooks/useLivoraToast";
import LivoraToast from "./common/LivoraToast";
import { defaultAvatarByGender } from "../utils/defaultAvatar";
import { formatNumberEn } from "../utils/digits";
import { CountryFlagGlyph } from "../utils/countryFlags";

// -------------------------------------------------------------
// 🌍 Countries list (keys only – names via i18n)
// -------------------------------------------------------------
const allCountriesList = [
  { key: "sa" },
  { key: "ae" },
  { key: "kw" },
  { key: "qa" },
  { key: "bh" },
  { key: "om" },
  { key: "tr" },
  { key: "ir" },
  { key: "ru" },
  { key: "ua" },
  { key: "at" },
  { key: "de" },
  { key: "ch" },
  { key: "fr" },
  { key: "it" },
  { key: "es" },
  { key: "nl" },
  { key: "be" },
  { key: "se" },
  { key: "no" },
  { key: "dk" },
  { key: "uk" },
  { key: "sy" },
  { key: "lb" },
  { key: "jo" },
  { key: "eg" },
  { key: "tn" },
  { key: "dz" },
  { key: "ma" },
];

// -------------------------------------------------------------
// ✅ VPN PLAN (B) helpers
// -------------------------------------------------------------
function getDisplayCountryKey(u) {
  const key = (u?.displayCountry || u?.countryKey || "")
    .toString()
    .trim()
    .toLowerCase();
  return /^[a-z]{2}$/.test(key) ? key : "";
}

function profileReceiveMode(u) {
  const r = String(
    u?.receiveMode ?? u?.settings?.messages?.receiveMode ?? "both"
  )
    .trim()
    .toLowerCase();
  if (r === "messages" || r === "off") return r;
  return "both";
}

function homeDebugLog(...args) {
  if (CALL_DEBUG) {
    console.log(...args);
  }
}

function debugOnlineUsersList(users, label, extra = {}) {
  if (!CALL_DEBUG) return;
  console.group(`ONLINE USERS DEBUG — ${label}`);
  if (Object.keys(extra).length) console.log("context:", extra);
  users.forEach((u) => {
    console.log({
      uid: u.id || u.uid,
      nickname: u.nickname || u.name || u.displayName || u.username,
      online: u.online,
      inApp: u.inApp,
      accountStatus: u.accountStatus,
      gender: u.gender,
      receiveMode: u?.receiveMode ?? u?.settings?.messages?.receiveMode,
      verificationStatus: u.verificationStatus,
      hidden: u.hidden,
      isHidden: u.isHidden,
      showInHome: u.showInHome,
      hasPhoto: !!(u.photoURL || u.profileImage),
    });
  });
  console.groupEnd();
}

function HomePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isReady } = useContext(AuthContext);

  const [onlineUsers, setOnlineUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  const [showCountryMenu, setShowCountryMenu] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState("all");

  const countryButtonRef = useRef(null);
  const countryMenuRef = useRef(null);

  const [myGender, setMyGender] = useState(null);
  const [blockedUids, setBlockedUids] = useState([]);

  // ✅ avatar cache: { uid: httpUrl }
  const [avatarMap, setAvatarMap] = useState({});

  const { begin: beginCallStart, logReturned, logFailed, starting, overlay } =
    useCallStartOverlay();
  const { toast, showToast } = useLivoraToast();

  useEffect(() => {
    if (!isReady) return;
    if (!user) {
      navigate("/");
    }
  }, [isReady, user, navigate]);

  // -------------------------------------------------------------
  // 🔹 Read current user gender
  // -------------------------------------------------------------
  useEffect(() => {
    if (!isReady || !user) return;

    const unsub = onSnapshot(doc(db, "users", user.uid), (snap) => {
      if (snap.exists()) {
        setMyGender(snap.data().gender || null);
      }
    });

    return () => unsub();
  }, [isReady, user]);

  // -------------------------------------------------------------
  // 🛑 Silent Block — users I blocked OR blocked me
  // -------------------------------------------------------------
  useEffect(() => {
    if (!isReady || !user) return;

    let alive = true;

    const loadBlockedUids = async () => {
      try {
        const myBlocksQ = query(
          collection(db, "blocks"),
          where("blockerUid", "==", user.uid)
        );

        const blockedMeQ = query(
          collection(db, "blocks"),
          where("blockedUid", "==", user.uid)
        );

        const [myBlocksSnap, blockedMeSnap] = await Promise.all([
          getDocs(myBlocksQ),
          getDocs(blockedMeQ),
        ]);

        const set = new Set();

        myBlocksSnap.forEach((d) => {
          const data = d.data() || {};
          if (data.blockedUid) set.add(data.blockedUid);
        });

        blockedMeSnap.forEach((d) => {
          const data = d.data() || {};
          if (data.blockerUid) set.add(data.blockerUid);
        });

        if (alive) {
          setBlockedUids([...set]);
        }
      } catch (err) {
        console.error("loadBlockedUids error:", err);
      }
    };

    loadBlockedUids();

    return () => {
      alive = false;
    };
  }, [isReady, user]);

  // -------------------------------------------------------------
  // 🔹 Fetch online users (WhatsApp-like: online + inApp)
  // -------------------------------------------------------------
  useEffect(() => {
    if (!isReady || !user || !myGender) return;

    const targetGender = myGender === "male" ? "female" : "male";

    const q = query(
      collection(db, "publicProfiles"),
      where("online", "==", true),
      where("inApp", "==", true),
      where("gender", "==", targetGender)
    );

    const unsub = onSnapshot(
      q,
      (snap) => {
        const list = [];
        const firestoreRaw = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

        homeDebugLog("[HomePage] Firestore query", {
          collection: "publicProfiles",
          where: [
            { field: "online", op: "==", value: true },
            { field: "inApp", op: "==", value: true },
            { field: "gender", op: "==", value: targetGender },
          ],
          orderBy: null,
          limit: null,
          myGender,
          targetGender,
          snapshotCount: firestoreRaw.length,
        });

        snap.forEach((d) => {
          if (d.id === user.uid) {
            homeDebugLog("FILTER OUT: self", d.id);
            return;
          }
          if (blockedUids.includes(d.id)) {
            homeDebugLog("FILTER OUT: blocked", d.id);
            return;
          }
          list.push({ id: d.id, ...d.data() });
        });

        debugOnlineUsersList(list, "HomePage after Firestore + self/blocked", {
          firestoreRawCount: firestoreRaw.length,
        });
        setOnlineUsers(list);
        setLoading(false);
      },
      (err) => {
        console.error("ONLINE USERS QUERY ERROR:", err);
        const message = err?.message || "";
        alert(
          t("home.online_users_query_error", {
            defaultValue: "ONLINE USERS QUERY ERROR: {{message}}",
            message,
          })
        );
        setLoading(false);
      }
    );

    return () => unsub();
  }, [isReady, user, myGender, blockedUids, t]);

  // -------------------------------------------------------------
  // ✅ Resolve avatars only for grid cells (first 9 of filtered list)
  // -------------------------------------------------------------
  const visibleUsers = useMemo(() => {
    const out = onlineUsers.filter((u) => {
      if (blockedUids.includes(u.id)) {
        homeDebugLog("FILTER OUT: blocked (visibleUsers)", u.id);
        return false;
      }
      return true;
    });
    debugOnlineUsersList(out, "HomePage visibleUsers");
    return out;
  }, [onlineUsers, blockedUids]);

  const filteredUsers = useMemo(() => {
    const out =
      selectedCountry === "all"
        ? visibleUsers
        : visibleUsers.filter((u) => {
            const countryKey = getDisplayCountryKey(u);
            if (countryKey !== selectedCountry) {
              homeDebugLog("FILTER OUT: country", u.id, {
                expected: selectedCountry,
                actual: countryKey,
              });
              return false;
            }
            return true;
          });
    debugOnlineUsersList(out, "HomePage filteredUsers (final list)", {
      selectedCountry,
    });
    return out;
  }, [visibleUsers, selectedCountry]);

  const gridUsers = useMemo(() => {
    const out = filteredUsers.slice(0, 9);
    filteredUsers.slice(9).forEach((u) => {
      homeDebugLog("FILTER OUT: home grid limit (slice 9)", u.id);
    });
    debugOnlineUsersList(out, "HomePage gridUsers (rendered cards)");
    return out;
  }, [filteredUsers]);

  useEffect(() => {
    let alive = true;

    const run = async () => {
      if (!gridUsers || gridUsers.length === 0) {
        if (alive) setAvatarMap({});
        return;
      }

      const entries = await Promise.all(
        gridUsers.map(async (u) => {
          const raw = u?.profileImage || "";
          const url = await resolveProfilePhotoUrl(raw, u?.gender);
          return [u.id, url];
        })
      );

      if (!alive) return;

      const map = {};
      for (const [uid, url] of entries) {
        if (uid) map[uid] = url;
      }
      setAvatarMap(map);
    };

    run();
    return () => {
      alive = false;
    };
  }, [gridUsers]);

  // -------------------------------------------------------------
  // Country menu: close on outside click / Escape
  // -------------------------------------------------------------
  useEffect(() => {
    if (!showCountryMenu) return undefined;

    const handlePointerDown = (event) => {
      const target = event.target;
      if (
        countryButtonRef.current?.contains(target) ||
        countryMenuRef.current?.contains(target)
      ) {
        return;
      }
      setShowCountryMenu(false);
    };

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setShowCountryMenu(false);
      }
    };

    document.addEventListener("pointerdown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [showCountryMenu]);

  // -------------------------------------------------------------
  // 📞 Start call
  // -------------------------------------------------------------
  const handleStartCall = async (targetUser) => {
    if (!isReady || !user) {
      alert(t("login_required", { defaultValue: "Please login first" }));
      return;
    }

    if (user.uid === targetUser.id) {
      alert(t("call_self_error", { defaultValue: "You can't call yourself" }));
      return;
    }

    if (profileReceiveMode(targetUser) !== "both") {
      return;
    }

    if (!beginCallStart()) return;

    const callButtonTapAt = performance.now();

    try {
      const fn = httpsCallable(
        getFunctions(undefined, "us-central1"),
        "startCall"
      );

      const res = await fn({ femaleUid: targetUser.id });
      const callId = res.data?.callId;

      if (!callId) {
        logFailed("no_call_id", { femaleUid: targetUser.id, data: res.data });
        alert(t("call_failed", { defaultValue: "Call failed" }));
        return;
      }

      startCallStartupTraceAt(callId, callButtonTapAt);
      logCallStartupEvent(callId, "call_button_tapped", "caller");
      logCallStartupEvent(callId, "call_created", "caller");

      logReturned(callId, { femaleUid: targetUser.id });
      const returnContext = buildReturnContextFromLocation(location);
      const uid = auth.currentUser?.uid;
      if (uid && returnContext) {
        saveReturnContext(uid, callId, returnContext);
        console.log(
          `[callReturn] captured role=caller callId=${callId} url=${returnContext.url}`
        );
      }
      navigate(`/call-outgoing/${callId}`, {
        state: {
          returnTo: returnContext?.url || "/home",
          returnContext: returnContext || undefined,
        },
      });
    } catch (e) {
      logFailed(e, { femaleUid: targetUser.id });
      const targetName = resolveDisplayName(targetUser, t);
      const { type, message } = getStartCallErrorToast(t, e, targetName);
      showToast(type, message);
    }
  };

  const currentCountryLabel =
    selectedCountry === "all" ? (
      t("all_countries", { defaultValue: "All countries" })
    ) : (
      <>
        <CountryFlagGlyph countryCode={selectedCountry} />{" "}
        {t(`countries.${selectedCountry}`, {
          defaultValue: selectedCountry.toUpperCase(),
        })}
      </>
    );

  const displayedOnlineCount = filteredUsers.length;
  const formattedOnlineCount = formatNumberEn(displayedOnlineCount);

  if (loading) {
    return (
      <div className="home-page home-page--loading">
        <div className="home-content">
          <div className="home-loading-content">
            <p className="home-loading-text">
              {t("loading_online_users", { defaultValue: "Loading..." })}
            </p>
          </div>
        </div>
        <BottomNav active="home" />
      </div>
    );
  }

  return (
    <div className="home-page">
      <div className="home-content">
        <div className="home-top-shell">
          <div className="home-top-grid">
            <div className="home-logo-slot">
              <span className="home-logo-mark" role="img" aria-label="Livora">
                <span className="home-logo-wordmark" aria-hidden>
                  <span className="home-logo-text home-logo-text--grad">
                    Livora
                  </span>
                </span>
              </span>
            </div>

            <button
              ref={countryButtonRef}
              className="home-country-pill"
              onClick={() => setShowCountryMenu((v) => !v)}
              type="button"
              aria-expanded={showCountryMenu}
              aria-haspopup="listbox"
            >
              <FaGlobe className="home-country-pill-icon" aria-hidden />
              <span className="home-country-pill-label">{currentCountryLabel}</span>
              <FaChevronDown className="home-country-pill-chevron" aria-hidden />
            </button>

            <div className="home-search-slot">
              <button
                className="home-search-btn home-search-btn--disabled"
                type="button"
                disabled
                aria-disabled="true"
                aria-label={t("home.search_disabled_aria", {
                  defaultValue: "Search — coming soon",
                })}
                title={t("home.search_coming_soon", {
                  defaultValue: "قريبًا",
                })}
              >
                <FaSearch aria-hidden />
              </button>
            </div>

            <section className="home-online-block">
              <div className="home-online-heading">
                <span className="home-online-dot" aria-hidden />
                <span className="home-online-label">
                  {t("home.online_now_label", { defaultValue: "Online now" })}
                </span>
              </div>
              <div className="home-online-count">{formattedOnlineCount}</div>
            </section>
          </div>

          {showCountryMenu && (
            <div
              ref={countryMenuRef}
              className="country-menu"
              role="listbox"
            >
              <button
                className={
                  selectedCountry === "all"
                    ? "country-menu-item active"
                    : "country-menu-item"
                }
                onClick={() => {
                  setSelectedCountry("all");
                  setShowCountryMenu(false);
                }}
                type="button"
              >
                🌍 {t("all_countries", { defaultValue: "All countries" })}
              </button>

              {allCountriesList.map((c) => (
                <button
                  key={c.key}
                  className={
                    selectedCountry === c.key
                      ? "country-menu-item active"
                      : "country-menu-item"
                  }
                  onClick={() => {
                    setSelectedCountry(c.key);
                    setShowCountryMenu(false);
                  }}
                  type="button"
                >
                  <CountryFlagGlyph countryCode={c.key} />{" "}
                  {t(`countries.${c.key}`, {
                    defaultValue: c.key.toUpperCase(),
                  })}
                </button>
              ))}
            </div>
          )}
        </div>

        <main className="home-users-grid">
          {filteredUsers.length === 0 ? (
            <p className="no-users">
              {t("no_online_users", { defaultValue: "No users online" })}
            </p>
          ) : (
            gridUsers.map((user) => {
              const key = getDisplayCountryKey(user);

              const label = key
                ? t(`countries.${key}`, { defaultValue: key.toUpperCase() })
                : user.countryName ||
                  user.country ||
                  t("unknown", { defaultValue: "—" });

              const receiveMode = profileReceiveMode(user);
              // Phase N9B: inCall is the server-authoritative Busy mirror
              // (see functions/calls/locks.js) - a Busy user stays visible
              // in this list (still online+inApp) but cannot be called.
              const isBusy = isUserBusy(user);
              const canCallUser = canCallUserPolicy(user);

              const goProfile = () => navigate(`/user/${user.id}`);

              return (
                <div key={user.id} className="home-user-card">
                  <div className="home-bubble-scene">
                    <div className="user-bubble-stack">
                      <div
                        className="user-bubble-ring-wrap"
                        onClick={goProfile}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            goProfile();
                          }
                        }}
                        role="button"
                        tabIndex={0}
                      >
                        <div className="user-bubble-ring" aria-hidden>
                          <div className="user-bubble-inner">
                            <img
                              src={avatarMap[user.id]}
                              alt={
                                user.name ||
                                user.displayName ||
                                user.username ||
                                t("user", { defaultValue: "User" })
                              }
                              className="user-bubble-img"
                              onError={(e) => {
                                e.currentTarget.src = defaultAvatarByGender(
                                  user.gender
                                );
                              }}
                            />
                          </div>
                        </div>

                        {isBusy ? (
                          <span
                            className="busy-dot"
                            role="img"
                            aria-label={t("presence.busy_now", { defaultValue: "Busy now" })}
                            title={t("presence.busy_now", { defaultValue: "Busy now" })}
                          />
                        ) : user.online && user.inApp ? (
                          <span className="online-dot" />
                        ) : null}
                      </div>

                      <div className="user-info-pill">
                        <div
                          className="user-info-pill-text"
                          onClick={goProfile}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              goProfile();
                            }
                          }}
                          role="button"
                          tabIndex={0}
                        >
                          <div className="user-name-pill">
                            {user.name ||
                              user.displayName ||
                              user.username ||
                              t("user", { defaultValue: "User" })}
                          </div>
                          <div className="user-country-pill">
                            {key ? (
                              <CountryFlagGlyph
                                countryCode={key}
                                className="user-country-flag"
                                imgClassName="user-country-flag country-flag-glyph__img"
                              />
                            ) : user.flagEmoji ? (
                              <span className="user-country-flag" aria-hidden>
                                {user.flagEmoji}
                              </span>
                            ) : (
                              <CountryFlagGlyph className="user-country-flag" />
                            )}{" "}
                            <span>{label}</span>
                          </div>
                          {isBusy && (
                            <div className="user-busy-pill">
                              {t("presence.busy_now", { defaultValue: "Busy now" })}
                            </div>
                          )}
                        </div>

                        <button
                          className={`user-video-button-pill${
                            canCallUser ? "" : " user-video-button-pill--off"
                          }`}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (starting) return;
                            const tap = resolveCallTapAction(user, t);
                            if (tap.action === "busy") {
                              showToast("error", tap.message);
                              return;
                            }
                            if (tap.action !== "call") return;
                            handleStartCall(user);
                          }}
                          type="button"
                          disabled={(receiveMode !== "both" && !isBusy) || starting}
                          aria-disabled={!canCallUser || starting}
                          aria-busy={starting}
                          title={
                            !canCallUser
                              ? isBusy || receiveMode === "off"
                                ? t("calls.user_unavailable", {
                                    defaultValue: "User unavailable",
                                  })
                                : t("calls_not_available", {
                                    defaultValue: "Calls not available",
                                  })
                              : undefined
                          }
                          aria-label={t("start_call", {
                            defaultValue: "Start call",
                          })}
                        >
                          <FaVideoCall />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </main>
      </div>

      <BottomNav active="home" />
      {overlay}
      <LivoraToast toast={toast} />
    </div>
  );
}

export default HomePage;